import React, { useLayoutEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import SidebarHeader from './components/SidebarHeader';
import SidebarNavigation from './components/SidebarNavigation';

// Cada página trae su propio <Layout>, así que al elegir una opción el menú se vuelve a montar y
// regresaba arriba (2-oct-2026, Carlos: «doy clic en Msg. Visitas y se me va para arriba»). La
// posición vive aquí, fuera del componente, y se restaura antes de pintar.
let menuScrollTop = 0;

const Sidebar: React.FC = () => {
  const { currentUser } = useAuth();
  const navRef = useRef<HTMLDivElement>(null);
  const restoring = useRef(true);
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    // Algunas opciones aparecen un instante después (datos del negocio): con el menú aún corto el
    // navegador recorta la posición. Se reaplica cada vez que crece, hasta que el usuario lo mueva.
    const target = menuScrollTop;
    el.scrollTop = target;
    const ro = new ResizeObserver(() => { if (restoring.current) el.scrollTop = target; });
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    const stop = () => { restoring.current = false; ro.disconnect(); };
    const t = window.setTimeout(stop, 1500);
    el.addEventListener('wheel', stop, { passive: true });
    el.addEventListener('touchstart', stop, { passive: true });
    el.addEventListener('pointerdown', stop);
    return () => {
      window.clearTimeout(t); ro.disconnect();
      el.removeEventListener('wheel', stop); el.removeEventListener('touchstart', stop); el.removeEventListener('pointerdown', stop);
    };
  }, []);
  
  const isAdmin = currentUser?.role === 'admin';
  const businessName = currentUser?.businessName;
  const logoUrl = currentUser?.logoUrl;

  return (
    // .sidebar-marca (index.css) redefine los tokens de color SOLO aquí dentro: barra navy de la marca
    // (rediseño opción B, 8-oct-2026) sin tocar cada botón del menú.
    <div className="sidebar-marca !bg-surface flex flex-col h-full w-64 border-r border-line">

      {/* Logo de Lealify (antes vivía en el encabezado) */}
      <div className="flex items-center gap-2 px-6 pt-6 pb-2 flex-shrink-0">
        <svg viewBox="0 0 310 120" className="h-8 w-auto" style={{ fill: '#ffffff' }} role="img" aria-label="Lealify">
          <path d="M262.5,120,272.21,98.96,249.435,44.573h14.586l15.4,38.445L294.6,44.573h14.044L276.764,120ZM146.57,94.406l-.326-1.3c-3.632,6.29-8.838,10.3-16.809,10.3-10.845,0-17.949-6.073-17.949-15.617,0-9.6,6.182-14.478,18.707-17.027l11.062-2.223c3.416-.651,4.989-2.006,4.989-5.314,0-5.206-3.687-8.405-9.868-8.405-6.4,0-9.869,2.874-10.737,7.537L113,61.492c1.193-9.977,9.869-18.166,23.913-18.166,9.382,0,16.484,3.361,19.684,9.327,1.572,2.983,2.44,6.561,2.44,12.851V88.766c0,2.332.977,3.416,2.712,3.525a12.729,12.729,0,0,0,2.006-.109V102.92a27.663,27.663,0,0,1-4.772.542C152.644,103.461,148.035,100.371,146.57,94.406Zm-6.127-17.352-7.538,2.007c-6.4,1.68-8.73,4.446-8.73,7.862,0,3.741,2.982,5.639,7.753,5.639,6.833,0,14.37-4.664,14.37-15.075V74.831C145.106,75.59,143.968,76.078,140.443,77.054ZM50.592,73.366c0-18,13.177-30.094,28.143-30.094,16.972,0,27.492,12.959,27.6,29.173a38.53,38.53,0,0,1-.325,4.989h-42.4c.868,8.3,7.213,14.261,16.16,14.261,6.453,0,10.519-3.2,12.526-7.537L104.6,85.946c-3.2,10.52-12.254,17.407-24.781,17.407C62.467,103.353,50.592,91.641,50.592,73.366Zm13.123-5.531H93.1c-1.355-7.646-6.127-12.851-14.532-12.851C71.252,54.985,65.179,60.081,63.715,67.836Zm144.13,34.216H170.592V25.54h12.8V89.417h11.875V44.573h12.743v57.479ZM0,102.052V0H43.543V76.512H30.2v-64.2H13.394V89.743H43.543v12.309ZM225.034,102V56.014H214.46v-11.5h10.628v-2.44c0-10.085,5.693-17.84,17.3-17.84a27.308,27.308,0,0,1,7.429.868l-1.193,11.5a16.918,16.918,0,0,0-4.555-.434c-4.066,0-6.29,2.006-6.29,6.4v2.007H248.4V56.014H237.776V102Zm-30.8-70.059a7.429,7.429,0,1,1,7.429,7.43A7.429,7.429,0,0,1,194.234,31.938Z" />
        </svg>
        <span className="text-[10px] font-semibold text-content-3 bg-white/10 px-1.5 py-0.5 rounded">
          {import.meta.env.VITE_APP_VERSION}
        </span>
      </div>

      {/* Sección del encabezado */}
      <div className="px-4 pt-3 pb-1 !bg-surface flex-shrink-0">
        <SidebarHeader
          isAdmin={isAdmin}
          businessName={businessName}
          logoUrl={logoUrl}
        />
      </div>
      
      {/* Sección de navegación */}
      {/* Barra de scroll TENUE pero SIEMPRE visible (24-ago-2026): estuvo oculta en todos los
          navegadores hasta que en la caja de Marrieto apareció un mouse sin rueda — sin barra
          no hay ninguna pista de que el menú se pueda arrastrar ni forma de hacerlo. thin +
          borde del tema; se marca un poco al pasar el cursor. */}
      <div ref={navRef} onScroll={(e) => { if (!restoring.current) menuScrollTop = e.currentTarget.scrollTop; }} className="flex-1 pt-2 px-2 pb-4 !bg-surface overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgb(var(--c-line))_transparent] hover:[scrollbar-color:rgb(var(--c-content-3))_transparent]">
        <SidebarNavigation />
      </div>
    </div>
  );
};

export default Sidebar;